import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getUserInteractions, OPTIMISTIC_INTERACTION_EVENT } from '../services/api';
import { useAuth } from './AuthContext';

const InteractionsContext = createContext();

const DEFAULT_INTERACTIONS = {
  liked: [],
  favorited: [],
  likedCollections: [],
  favoritedCollections: [],
  likedContexts: [],
  favoritedContexts: [],
  used: [],
  usedCollections: [],
  usedContexts: [],
};

const INTERACTIONS_TTL_MS = 2 * 60 * 1000;

const OPTIMISTIC_LIST_KEYS = new Set([
  'liked',
  'favorited',
  'likedCollections',
  'favoritedCollections',
  'likedContexts',
  'favoritedContexts',
]);

export const InteractionsProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [interactions, setInteractions] = useState(DEFAULT_INTERACTIONS);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const inFlightRef = useRef(null);
  const lastUserIdRef = useRef(null);
  const interactionsRef = useRef(interactions);

  useEffect(() => {
    interactionsRef.current = interactions;
  }, [interactions]);

  const resetInteractions = useCallback(() => {
    setInteractions(DEFAULT_INTERACTIONS);
    setLastFetchedAt(0);
    setError(null);
    setVersion((prev) => prev + 1);
    inFlightRef.current = null;
  }, []);

  useEffect(() => {
    const nextUserId = user?.id || null;
    if (lastUserIdRef.current !== nextUserId) {
      lastUserIdRef.current = nextUserId;
      resetInteractions();
    }
  }, [user?.id, resetInteractions]);

  const isFresh = useCallback(() => {
    if (!lastFetchedAt) return false;
    return Date.now() - lastFetchedAt < INTERACTIONS_TTL_MS;
  }, [lastFetchedAt]);

  const fetchInteractions = useCallback(async ({ force = false } = {}) => {
    if (!token || !user?.id) {
      resetInteractions();
      return DEFAULT_INTERACTIONS;
    }

    if (!force && isFresh()) {
      return interactionsRef.current;
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    setIsFetching(true);
    setError(null);

    const requestPromise = getUserInteractions()
      .then((response) => {
        const next = response?.interactions || DEFAULT_INTERACTIONS;
        setInteractions(next);
        setLastFetchedAt(Date.now());
        setVersion((prev) => prev + 1);
        return next;
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load interactions');
        return interactionsRef.current;
      })
      .finally(() => {
        setIsFetching(false);
        inFlightRef.current = null;
      });

    inFlightRef.current = requestPromise;
    return requestPromise;
  }, [token, user?.id, isFresh, resetInteractions]);

  const invalidateInteractions = useCallback(() => {
    setLastFetchedAt(0);
  }, []);

  const applyOptimisticInteractionUpdate = useCallback((updater) => {
    setInteractions((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : prev;
      if (!next) return prev;
      return {
        ...DEFAULT_INTERACTIONS,
        ...next,
      };
    });
    setVersion((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const onOptimisticInteraction = (e) => {
      const { key, id, add } = e.detail || {};
      if (!key || !OPTIMISTIC_LIST_KEYS.has(key) || id === undefined || id === null) return;
      const idStr = String(id);
      applyOptimisticInteractionUpdate((prev) => {
        const arr = [...(prev[key] || [])];
        const has = arr.map(String).includes(idStr);
        if (add && !has) {
          return { ...prev, [key]: [...arr, id] };
        }
        if (!add && has) {
          return { ...prev, [key]: arr.filter((x) => String(x) !== idStr) };
        }
        return prev;
      });
    };
    window.addEventListener(OPTIMISTIC_INTERACTION_EVENT, onOptimisticInteraction);
    return () => {
      window.removeEventListener(OPTIMISTIC_INTERACTION_EVENT, onOptimisticInteraction);
    };
  }, [applyOptimisticInteractionUpdate]);

  const value = useMemo(() => ({
    interactions,
    lastFetchedAt,
    isFetching,
    error,
    version,
    isFresh: isFresh(),
    fetchInteractions,
    invalidateInteractions,
    applyOptimisticInteractionUpdate,
    resetInteractions,
  }), [
    interactions,
    lastFetchedAt,
    isFetching,
    error,
    version,
    isFresh,
    fetchInteractions,
    invalidateInteractions,
    applyOptimisticInteractionUpdate,
    resetInteractions,
  ]);

  return (
    <InteractionsContext.Provider value={value}>
      {children}
    </InteractionsContext.Provider>
  );
};

export const useInteractions = () => {
  const context = useContext(InteractionsContext);
  if (!context) {
    throw new Error('useInteractions must be used within an InteractionsProvider');
  }
  return context;
};

export default InteractionsContext;
